import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from 'firebase/auth';
import { useHostedServer } from '../hooks/useHostedServer';
import { useHostedServerTlsReady } from '../hooks/useHostedServerTlsReady';

interface HostedServerPanelProps {
  user: User | null;
  variant?: 'default' | 'embedded';
}

const HostedServerPanel: React.FC<HostedServerPanelProps> = ({ user, variant = 'default' }) => {
  const isEmbedded = variant === 'embedded';
  const { t } = useTranslation();
  const {
    server,
    isLoading,
    isBusy,
    createServer,
    startServer,
    touchServer
  } = useHostedServer(user);
  const {
    tlsReady,
    isCheckingTls,
    tlsCheckTimedOut,
    recheckTls
  } = useHostedServerTlsReady(server);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleAction = async (action: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await action();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'REQUEST_FAILED';
      setActionError(message);
    }
  };

  const openActual = () => {
    if (!server?.serverUrl || !tlsReady) return;
    void touchServer().catch(() => {
      // Non-blocking: opening Actual should still work if touch fails
    });
    window.open(server.serverUrl, '_blank', 'noopener,noreferrer');
  };

  const isProvisioning = server?.status === 'provisioning';
  const showProvisioningState = isProvisioning || (isBusy && !server);
  const showTlsWaitingState = server?.status === 'active' && isCheckingTls;
  const canOpenActual = server?.status === 'active' && tlsReady;

  return (
    <div className={`hosted-server-panel${isEmbedded ? ' embedded' : ''}`}>
      {!isEmbedded && (
        <>
          <h4>{t('hosted.title', { ns: 'serverManagement' })}</h4>
          <p className="hosted-server-description">{t('hosted.description', { ns: 'serverManagement' })}</p>
        </>
      )}

      <ul className="hosted-trust-list">
        <li>{t('hosted.trustNoServerPassword', { ns: 'serverManagement' })}</li>
        <li>{t('hosted.trustNoEncryptionPassword', { ns: 'serverManagement' })}</li>
        <li>{t('hosted.trustPasswordRecovery', { ns: 'serverManagement' })}</li>
        <li>{t('hosted.trustYouControlAuth', { ns: 'serverManagement' })}</li>
      </ul>

      {isLoading && (
        <div className="hosted-server-loading">
          <div className="spinner" />
          <p>{t('hosted.loading', { ns: 'serverManagement' })}</p>
        </div>
      )}

      {!isLoading && !server && (
        <div className="hosted-server-empty">
          <button
            type="button"
            className="action-button primary"
            disabled={isBusy || !user}
            onClick={() => handleAction(createServer)}
          >
            {isBusy ? (
              <span className="hosted-button-content">
                <span className="spinner spinner-inline" aria-hidden="true" />
                {t('hosted.creating', { ns: 'serverManagement' })}
              </span>
            ) : (
              t('hosted.create', { ns: 'serverManagement' })
            )}
          </button>
        </div>
      )}

      {server && (
        <div className="hosted-server-card">
          <div className="hosted-server-status">
            <strong>{t('hosted.statusLabel', { ns: 'serverManagement' })}</strong>{' '}
            <span className={`hosted-status-badge status-${server.status}`}>
              {t(`hosted.status.${server.status}`, { ns: 'serverManagement' })}
            </span>
          </div>

          {server.serverUrl && (
            <div className="hosted-server-url">
              <label>{t('hosted.urlLabel', { ns: 'serverManagement' })}</label>
              <code>{server.serverUrl}</code>
            </div>
          )}

          {server.lastUsedAt && (
            <div className="hosted-server-last-used">
              <strong>{t('hosted.lastUsedLabel', { ns: 'serverManagement' })}</strong>{' '}
              <span>
                {new Date(server.lastUsedAt).toLocaleString()}
                {server.lastUsedSource
                  ? ` (${t(`hosted.lastUsedSource.${server.lastUsedSource}`, {
                      ns: 'serverManagement',
                      defaultValue: server.lastUsedSource
                    })})`
                  : ''}
              </span>
            </div>
          )}

          {showProvisioningState && (
            <div className="hosted-server-loading">
              <div className="spinner" />
              <p>{t('hosted.provisioning', { ns: 'serverManagement' })}</p>
            </div>
          )}

          {showTlsWaitingState && (
            <div className="hosted-server-loading">
              <div className="spinner" />
              <p>{t('hosted.securingConnection', { ns: 'serverManagement' })}</p>
            </div>
          )}

          {tlsCheckTimedOut && (
            <div className="hosted-server-tls-timeout">
              <p>{t('hosted.securingConnectionDelayed', { ns: 'serverManagement' })}</p>
              <button
                type="button"
                className="action-button secondary"
                onClick={() => recheckTls()}
              >
                {t('hosted.checkAgain', { ns: 'serverManagement' })}
              </button>
            </div>
          )}

          {server.status === 'failed' && server.lastError && (
            <p className="hosted-server-error">{server.lastError}</p>
          )}

          <div className="hosted-server-actions">
            {server.status === 'active' && (
              <button
                type="button"
                className="action-button primary"
                disabled={!canOpenActual}
                onClick={openActual}
              >
                {isCheckingTls ? (
                  <span className="hosted-button-content">
                    <span className="spinner spinner-inline" aria-hidden="true" />
                    {t('hosted.preparingOpenActual', { ns: 'serverManagement' })}
                  </span>
                ) : (
                  t('hosted.openActual', { ns: 'serverManagement' })
                )}
              </button>
            )}

            {server.status === 'stopped' && (
              <button
                type="button"
                className="action-button primary"
                disabled={isBusy}
                onClick={() => handleAction(startServer)}
              >
                {isBusy ? (
                  <span className="hosted-button-content">
                    <span className="spinner spinner-inline" aria-hidden="true" />
                    {t('hosted.starting', { ns: 'serverManagement' })}
                  </span>
                ) : (
                  t('hosted.start', { ns: 'serverManagement' })
                )}
              </button>
            )}

            {server.status === 'failed' && (
              <button
                type="button"
                className="action-button secondary"
                disabled={isBusy}
                onClick={() => handleAction(createServer)}
              >
                {isBusy ? (
                  <span className="hosted-button-content">
                    <span className="spinner spinner-inline" aria-hidden="true" />
                    {t('hosted.creating', { ns: 'serverManagement' })}
                  </span>
                ) : (
                  t('hosted.retry', { ns: 'serverManagement' })
                )}
              </button>
            )}

          </div>
        </div>
      )}

      {actionError && (
        <p className="hosted-server-error">
          {actionError === 'Hosted Actual servers are temporarily full. Please try again later.'
            ? t('hosted.capacityFull', { ns: 'serverManagement' })
            : actionError === 'AUTH_REQUIRED'
              ? t('authRequiredForServerManagement', { ns: 'auth' })
              : actionError}
        </p>
      )}
    </div>
  );
};

export default HostedServerPanel;
